import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Search, Plus, Edit, Trash2, Building, Loader2, Clock, MapPin } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Programs = () => {
    const [programs, setPrograms] = useState([]);
    const [feeBands, setFeeBands] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingProgram, setEditingProgram] = useState(null);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        short_name: '',
        days: [],
        start_time: '',
        end_time: '',
        location: '',
        days_per_term: 0,
        fee_band_id: '',
        order: 0,
        gender_eligibility: 'both',
        min_age: '',
        max_age: '',
        day_schedule: [],
        show_in_renewal: true,
        next_year_schedule: [],
        next_year_note: '',
    });

    const daysOfWeek = ['Saturday', 'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [programsRes, feeBandsRes] = await Promise.all([
                axios.get(`${API_URL}/api/programs`, { withCredentials: true }),
                axios.get(`${API_URL}/api/fees/bands`, { withCredentials: true })
            ]);
            setPrograms(programsRes.data);
            setFeeBands(feeBandsRes.data);
        } catch (error) {
            toast.error('Failed to load programs');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                ...formData,
                min_age: formData.min_age === '' ? null : parseInt(formData.min_age, 10),
                max_age: formData.max_age === '' ? null : parseInt(formData.max_age, 10),
                day_schedule: (formData.day_schedule && formData.day_schedule.length > 0) ? formData.day_schedule : null,
                next_year_schedule: (formData.next_year_schedule && formData.next_year_schedule.length > 0) ? formData.next_year_schedule : null,
                next_year_note: (formData.next_year_note || '').trim() || null,
                show_in_renewal: formData.show_in_renewal !== false,
            };
            if (editingProgram) {
                await axios.put(`${API_URL}/api/programs/${editingProgram.id}`, payload, { withCredentials: true });
                toast.success('Program updated successfully');
            } else {
                await axios.post(`${API_URL}/api/programs`, payload, { withCredentials: true });
                toast.success('Program created successfully');
            }
            setDialogOpen(false);
            resetForm();
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save program');
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = (program) => {
        setEditingProgram(program);
        setFormData({
            name: program.name,
            short_name: program.short_name,
            days: program.days || [],
            start_time: program.start_time,
            end_time: program.end_time,
            location: program.location,
            days_per_term: program.days_per_term,
            fee_band_id: program.fee_band_id,
            order: program.order ?? 0,
            gender_eligibility: program.gender_eligibility || 'both',
            min_age: program.min_age ?? '',
            max_age: program.max_age ?? '',
            day_schedule: program.day_schedule || [],
            show_in_renewal: program.show_in_renewal !== false,
            next_year_schedule: program.next_year_schedule || [],
            next_year_note: program.next_year_note || '',
        });
        setDialogOpen(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this program?')) return;
        try {
            await axios.delete(`${API_URL}/api/programs/${id}`, { withCredentials: true });
            toast.success('Program deleted');
            fetchData();
        } catch (error) {
            toast.error('Failed to delete program');
        }
    };

    const resetForm = () => {
        setEditingProgram(null);
        setFormData({
            name: '',
            short_name: '',
            days: [],
            start_time: '',
            end_time: '',
            location: '',
            days_per_term: 0,
            fee_band_id: '',
            order: 0,
            gender_eligibility: 'both',
            min_age: '',
            max_age: '',
            day_schedule: [],
            show_in_renewal: true,
            next_year_schedule: [],
            next_year_note: '',
        });
    };

    const toggleDay = (day) => {
        setFormData(prev => {
            const has = prev.days.includes(day);
            const newDays = has ? prev.days.filter(d => d !== day) : [...prev.days, day];
            // Keep `day_schedule` in sync with the selected days
            let newSchedule = (prev.day_schedule || []).filter(s => newDays.includes(s.day));
            newDays.forEach(d => {
                if (!newSchedule.find(s => s.day === d)) {
                    newSchedule.push({ day: d, start_time: prev.start_time || '', end_time: prev.end_time || '' });
                }
            });
            // Preserve order by week
            const weekOrder = ['Saturday', 'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
            newSchedule.sort((a, b) => weekOrder.indexOf(a.day) - weekOrder.indexOf(b.day));
            return { ...prev, days: newDays, day_schedule: newSchedule };
        });
    };

    const updateDayTime = (day, field, value) => {
        setFormData(prev => ({
            ...prev,
            day_schedule: (prev.day_schedule || []).map(s => s.day === day ? { ...s, [field]: value } : s),
        }));
    };

    const updateNextYearDayTime = (day, field, value) => {
        setFormData(prev => ({
            ...prev,
            next_year_schedule: (prev.next_year_schedule || []).map(s => s.day === day ? { ...s, [field]: value } : s),
        }));
    };

    const copyCurrentScheduleToNextYear = () => {
        setFormData(prev => {
            const src = (prev.day_schedule && prev.day_schedule.length)
                ? prev.day_schedule.map(s => ({ ...s }))
                : (prev.days || []).map(d => ({ day: d, start_time: prev.start_time || '', end_time: prev.end_time || '' }));
            return { ...prev, next_year_schedule: src };
        });
    };

    const clearNextYearSchedule = () => setFormData(prev => ({ ...prev, next_year_schedule: [] }));

    const filteredPrograms = programs
        .filter(p =>
            p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            p.short_name?.toLowerCase().includes(searchQuery.toLowerCase())
        )
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || (a.name || '').localeCompare(b.name || ''));

    const getFeeBandName = (id) => feeBands.find(fb => fb.id === id)?.name || 'N/A';

    if (loading) {
        return (
            <DashboardLayout>
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#064E3B]"></div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="programs-page">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-[#1F2937] font-['Outfit']">Programs</h1>
                        <p className="text-[#4B5563]">Manage school programs</p>
                    </div>
                    <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-[#064E3B] hover:bg-[#047857]" data-testid="add-program-btn">
                        <Plus className="h-4 w-4 mr-2" /> Add Program
                    </Button>
                </div>

                <Card className="card-surface">
                    <CardContent className="p-4">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#4B5563]" />
                            <Input
                                placeholder="Search programs..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-9"
                            />
                        </div>
                    </CardContent>
                </Card>

                <Card className="card-surface">
                    <CardContent className="p-0">
                        {filteredPrograms.length > 0 ? (
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Program</TableHead>
                                            <TableHead>Days</TableHead>
                                            <TableHead>Time</TableHead>
                                            <TableHead>Location</TableHead>
                                            <TableHead>Fee Band</TableHead>
                                            <TableHead className="text-right">Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {filteredPrograms.map((program) => (
                                            <TableRow key={program.id}>
                                                <TableCell>
                                                    <div className="flex items-center space-x-3">
                                                        <div className="h-10 w-10 rounded-lg bg-[#064E3B]/10 flex items-center justify-center">
                                                            <Building className="h-5 w-5 text-[#064E3B]" />
                                                        </div>
                                                        <div>
                                                            <p className="font-medium">{program.name}</p>
                                                            <div className="flex items-center gap-1.5 mt-0.5">
                                                                <p className="text-xs text-[#4B5563]">{program.short_name}</p>
                                                                {program.show_in_renewal === false && (
                                                                    <Badge variant="outline" className="text-[10px] py-0 px-1.5 border-[#9CA3AF] text-[#6B7280]">renewal: off</Badge>
                                                                )}
                                                                {program.show_in_renewal !== false && (program.next_year_schedule || program.next_year_note) && (
                                                                    <Badge variant="outline" className="text-[10px] py-0 px-1.5 border-[#D97706] text-[#92400E]">NY override</Badge>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex flex-wrap gap-1">
                                                        {program.days?.map(day => (
                                                            <Badge key={day} variant="outline" className="text-xs">{day.slice(0,3)}</Badge>
                                                        ))}
                                                    </div>
                                                </TableCell>
                                                <TableCell className="tabular-nums">
                                                    <div className="flex items-center">
                                                        <Clock className="h-3 w-3 mr-1 text-[#4B5563]" />
                                                        {program.start_time} - {program.end_time}
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center">
                                                        <MapPin className="h-3 w-3 mr-1 text-[#4B5563]" />
                                                        {program.location}
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <Badge className="bg-[#D97706]">{getFeeBandName(program.fee_band_id)}</Badge>
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <div className="flex justify-end space-x-2">
                                                        <Button size="sm" variant="outline" onClick={() => handleEdit(program)} data-testid={`edit-${program.id}`}>
                                                            <Edit className="h-4 w-4" />
                                                        </Button>
                                                        <Button size="sm" variant="outline" className="text-[#EF4444]" onClick={() => handleDelete(program.id)} data-testid={`delete-${program.id}`}>
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        ) : (
                            <div className="flex flex-col items-center justify-center py-12">
                                <Building className="h-12 w-12 text-[#4B5563]/30 mb-4" />
                                <p className="text-[#4B5563] mb-4">No programs found</p>
                                <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-[#064E3B] hover:bg-[#047857]">
                                    <Plus className="h-4 w-4 mr-2" /> Create First Program
                                </Button>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Add/Edit Dialog */}
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="font-['Outfit']">{editingProgram ? 'Edit Program' : 'Add New Program'}</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Program Name</Label>
                                    <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required data-testid="program-name" />
                                </div>
                                <div className="space-y-2">
                                    <Label>Short Name</Label>
                                    <Input value={formData.short_name} onChange={(e) => setFormData({...formData, short_name: e.target.value})} required data-testid="program-short-name" />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label>Days</Label>
                                <div className="flex flex-wrap gap-2">
                                    {daysOfWeek.map(day => (
                                        <Badge
                                            key={day}
                                            variant={formData.days.includes(day) ? 'default' : 'outline'}
                                            className={`cursor-pointer ${formData.days.includes(day) ? 'bg-[#064E3B]' : ''}`}
                                            onClick={() => toggleDay(day)}
                                        >
                                            {day}
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Default Start Time</Label>
                                    <Input type="time" value={formData.start_time} onChange={(e) => setFormData({...formData, start_time: e.target.value})} required />
                                </div>
                                <div className="space-y-2">
                                    <Label>Default End Time</Label>
                                    <Input type="time" value={formData.end_time} onChange={(e) => setFormData({...formData, end_time: e.target.value})} required />
                                </div>
                            </div>

                            {formData.days.length > 1 && (
                                <div className="space-y-2 bg-[#F9FAFB] rounded-lg p-3 border border-[#E5E7EB]" data-testid="per-day-schedule">
                                    <Label className="text-xs text-[#4B5563]">Per-day schedule (overrides default times)</Label>
                                    <div className="space-y-2">
                                        {(formData.day_schedule || []).map(s => (
                                            <div key={s.day} className="grid grid-cols-[100px_1fr_1fr] gap-2 items-center text-sm">
                                                <span className="font-medium text-[#1F2937]">{s.day}</span>
                                                <Input type="time" value={s.start_time} onChange={(e) => updateDayTime(s.day, 'start_time', e.target.value)} data-testid={`day-${s.day}-start`} />
                                                <Input type="time" value={s.end_time} onChange={(e) => updateDayTime(s.day, 'end_time', e.target.value)} data-testid={`day-${s.day}-end`} />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="space-y-2">
                                <Label>Location</Label>
                                <Input value={formData.location} onChange={(e) => setFormData({...formData, location: e.target.value})} required />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Days per Term</Label>
                                    <Input type="number" value={formData.days_per_term} onChange={(e) => setFormData({...formData, days_per_term: parseInt(e.target.value) || 0})} required />
                                </div>
                                <div className="space-y-2">
                                    <Label>Fee Band</Label>
                                    <Select value={formData.fee_band_id} onValueChange={(v) => setFormData({...formData, fee_band_id: v})}>
                                        <SelectTrigger><SelectValue placeholder="Select fee band" /></SelectTrigger>
                                        <SelectContent>
                                            {feeBands.map(fb => (
                                                <SelectItem key={fb.id} value={fb.id}>{fb.name} (€{fb.first_student_fee})</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="grid grid-cols-3 gap-4 pt-2 border-t border-[#E5E7EB]">
                                <div className="space-y-2">
                                    <Label>Display Order</Label>
                                    <Input type="number" value={formData.order} onChange={(e) => setFormData({...formData, order: parseInt(e.target.value) || 0})} placeholder="0" data-testid="program-order" />
                                    <p className="text-xs text-[#6B7280]">Lower numbers show first</p>
                                </div>
                                <div className="space-y-2">
                                    <Label>Gender Eligibility</Label>
                                    <Select value={formData.gender_eligibility} onValueChange={(v) => setFormData({...formData, gender_eligibility: v})}>
                                        <SelectTrigger data-testid="program-gender"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="both">Both</SelectItem>
                                            <SelectItem value="male">Male only</SelectItem>
                                            <SelectItem value="female">Female only</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label>Age Range (years)</Label>
                                    <div className="flex items-center gap-2">
                                        <Input type="number" min="0" max="100" placeholder="min" value={formData.min_age} onChange={(e) => setFormData({...formData, min_age: e.target.value})} data-testid="program-min-age" />
                                        <span className="text-[#6B7280]">–</span>
                                        <Input type="number" min="0" max="100" placeholder="max" value={formData.max_age} onChange={(e) => setFormData({...formData, max_age: e.target.value})} data-testid="program-max-age" />
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-3 bg-[#FFFBEB] border border-[#FDE68A] rounded-lg p-3">
                                <div className="flex items-center justify-between gap-3">
                                    <div className="flex-1">
                                        <Label className="text-sm font-semibold">Renewal page settings</Label>
                                        <p className="text-xs text-[#92400E]">Controls how this program appears to parents on the next-year renewal page.</p>
                                    </div>
                                    <label className="flex items-center gap-2 cursor-pointer text-sm" data-testid="show-in-renewal-toggle">
                                        <input
                                            type="checkbox"
                                            checked={formData.show_in_renewal !== false}
                                            onChange={(e) => setFormData({...formData, show_in_renewal: e.target.checked})}
                                            className="h-4 w-4"
                                        />
                                        <span className="font-medium">Show in renewal</span>
                                    </label>
                                </div>
                                {formData.show_in_renewal !== false && (
                                    <>
                                        <div className="space-y-2">
                                            <Label className="text-xs">Next year note <span className="text-[#6B7280] font-normal">— optional. Shown alongside the program on renewal (e.g. "Same as current year — Sat 10:00–12:00").</span></Label>
                                            <Input
                                                value={formData.next_year_note || ''}
                                                onChange={(e) => setFormData({...formData, next_year_note: e.target.value})}
                                                placeholder="e.g. New time: Sat 11:00–13:00 from September"
                                                data-testid="next-year-note"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <div className="flex items-center justify-between">
                                                <Label className="text-xs">Next year per-day schedule <span className="text-[#6B7280] font-normal">— overrides the times above on the renewal page</span></Label>
                                                <div className="flex gap-2">
                                                    <Button type="button" size="sm" variant="outline" onClick={copyCurrentScheduleToNextYear} disabled={(formData.days || []).length === 0} data-testid="copy-current-schedule">
                                                        Copy current
                                                    </Button>
                                                    {(formData.next_year_schedule || []).length > 0 && (
                                                        <Button type="button" size="sm" variant="ghost" onClick={clearNextYearSchedule} className="text-[#EF4444]" data-testid="clear-next-year">
                                                            Clear
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                            {(formData.next_year_schedule || []).length === 0 ? (
                                                <p className="text-xs text-[#6B7280] italic px-2 py-2 bg-white rounded border border-[#E5E7EB]">No override — current schedule above will be shown.</p>
                                            ) : (
                                                <div className="bg-white rounded border border-[#E5E7EB] p-2 space-y-1.5">
                                                    {formData.next_year_schedule.map(s => (
                                                        <div key={s.day} className="grid grid-cols-[100px_1fr_1fr] gap-2 items-center text-sm">
                                                            <span className="font-medium text-[#1F2937]">{s.day}</span>
                                                            <Input type="time" value={s.start_time} onChange={(e) => updateNextYearDayTime(s.day, 'start_time', e.target.value)} data-testid={`ny-${s.day}-start`} />
                                                            <Input type="time" value={s.end_time} onChange={(e) => updateNextYearDayTime(s.day, 'end_time', e.target.value)} data-testid={`ny-${s.day}-end`} />
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>

                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                                <Button type="submit" disabled={saving} className="bg-[#064E3B] hover:bg-[#047857]" data-testid="save-program">
                                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                                    {editingProgram ? 'Update' : 'Create'}
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>
        </DashboardLayout>
    );
};

export default Programs;
